export const AUTH_PRIMARY = '#FF0100';
export const AUTH_PRIMARY_HOVER = '#CC0000';

/** Shared corner radius for inputs, primary CTA, and Google sign-in on auth screens */
export const authControlRadiusSx = { borderRadius: 2 } as const;

/** Decorative layer for Google sign-in (real click target is invisible GIS iframe on top). */
export const authGoogleButtonVisualSx = {
  ...authControlRadiusSx,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
  height: 44,
  border: '1px solid #DADCE0',
  bgcolor: '#fff',
  color: '#3c4043',
  fontWeight: 500,
  fontSize: '0.9375rem',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  userSelect: 'none',
} as const;

export const authPageSx = {
  minHeight: '100dvh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'safe center',
  bgcolor: '#F4F6F8',
  py: { xs: 3, sm: 4 },
  px: 2,
  boxSizing: 'border-box',
  overflowY: 'auto',
} as const;

export const authCardSx = {
  p: { xs: 3, sm: 4 },
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 3,
  border: '1px solid #E8EAED',
  boxShadow: '0 8px 32px rgba(15, 17, 17, 0.08)',
  bgcolor: '#fff',
  color: '#0F1111',
} as const;

export const authLogoSx = {
  width: { xs: 200, sm: 240 },
  height: 'auto',
  mb: 2.5,
} as const;

export const authTitleSx = {
  mb: 0.5,
  fontWeight: 700,
  color: '#0F1111',
  textAlign: 'center',
} as const;

export const authSubtitleSx = {
  mb: 3,
  color: '#565959',
  textAlign: 'center',
  maxWidth: 360,
} as const;

const AUTH_INPUT_TEXT = '#0F1111';
const AUTH_INPUT_BG = '#FAFAFA';

/** Auth screens always use a light card; force input colors so dark app theme does not yield white-on-white text. */
export const authFieldSx = {
  mb: 2,
  '& .MuiInputLabel-root': {
    color: '#565959',
    '&.Mui-focused': { color: AUTH_PRIMARY },
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: AUTH_INPUT_BG,
    color: AUTH_INPUT_TEXT,
    '& fieldset': { borderColor: '#DADCE0' },
    '&:hover fieldset': { borderColor: '#BDBDBD' },
    '&.Mui-focused fieldset': { borderColor: AUTH_PRIMARY, borderWidth: 2 },
    '& input, & textarea': {
      color: AUTH_INPUT_TEXT,
      WebkitTextFillColor: AUTH_INPUT_TEXT,
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 100px ${AUTH_INPUT_BG} inset`,
      WebkitTextFillColor: AUTH_INPUT_TEXT,
      caretColor: AUTH_INPUT_TEXT,
    },
  },
  '& .MuiIconButton-root': {
    color: '#565959',
  },
} as const;

export const authPrimaryButtonSx = {
  py: 1.5,
  borderRadius: 2,
  fontWeight: 600,
  fontSize: '0.9375rem',
  bgcolor: AUTH_PRIMARY,
  color: '#fff',
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { bgcolor: AUTH_PRIMARY_HOVER, boxShadow: 'none' },
  '&:disabled': { bgcolor: '#E8EAED', color: '#9AA0A6' },
} as const;

export const authSectionLabelSx = {
  width: '100%',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#565959',
  mb: 1,
  mt: 0.5,
} as const;

export const authDividerRowSx = {
  display: 'flex',
  alignItems: 'center',
  my: 2.5,
  width: '100%',
} as const;

export const authLinkSx = {
  color: AUTH_PRIMARY,
  textDecoration: 'none',
  fontWeight: 600,
  '&:hover': { textDecoration: 'underline', color: AUTH_PRIMARY_HOVER },
} as const;

export const authMutedLinkSx = {
  color: '#565959',
  textDecoration: 'none',
  fontSize: '0.875rem',
  '&:hover': { textDecoration: 'underline', color: '#0F1111' },
} as const;

export const phoneInputStyles = {
  inputStyle: {
    width: '100%',
    height: '56px',
    borderColor: '#C4C4C4',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: '#FAFAFA',
  },
  buttonStyle: {
    borderColor: '#C4C4C4',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    backgroundColor: '#FAFAFA',
  },
  containerStyle: { width: '100%' },
} as const;

export const otpBoxSx = (filled: boolean, focused?: boolean) => ({
  width: { xs: 44, sm: 48 },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: filled ? '#FFF5F5' : '#FAFAFA',
    '& fieldset': {
      borderColor: focused ? AUTH_PRIMARY : filled ? '#FFBFBF' : '#DADCE0',
      borderWidth: focused ? 2 : 1,
    },
    '&:hover fieldset': { borderColor: AUTH_PRIMARY },
  },
  '& input': {
    textAlign: 'center',
    fontSize: '1.35rem',
    fontWeight: 700,
    padding: '12px 0',
    color: '#0F1111',
  },
});
